import { useEffect, useMemo, useState } from 'react';
import { RefreshCw } from 'lucide-react';

import { useAuth } from '@/lib/auth/AuthProvider';
import type { AdminStorefrontDetail, AdminStorefrontEvent } from '@/lib/api/storefrontAdminHooks';

import { eventKey, eventSentence, productNameMap } from './storeChanges';
import { faqSentence } from './storeDiff';
import { shortDateTime } from './storefrontFormat';
import { ActionButton } from './ui';

/**
 * Changes someone else made while this page was open: another admin's
 * review or status action, or an owner edit picked up by a refetch (a
 * review dialog re-reads the store when it opens, a stale action reloads
 * it). The admin's own actions are taken as seen. Until the admin has
 * looked (`acknowledge`), review and status actions stay off, so a
 * decision is never made on copy the admin has not seen.
 */
export function useUnseenStoreChanges(store: AdminStorefrontDetail) {
  const { session } = useAuth();
  const me = session?.user.email?.toLowerCase() ?? null;
  const [seen, setSeen] = useState(() => new Set(store.events.map((e) => eventKey(e))));
  const unseen = useMemo(
    () => store.events.filter((e) => !seen.has(eventKey(e))),
    [store.events, seen],
  );
  const foreign = useMemo(
    () => unseen.filter((e) => !me || (e.actor ?? '').toLowerCase() !== me),
    [unseen, me],
  );
  // The admin's own actions need no notice.
  useEffect(() => {
    if (unseen.length > 0 && foreign.length === 0) {
      setSeen(new Set(store.events.map((e) => eventKey(e))));
    }
  }, [unseen.length, foreign.length, store.events]);
  return {
    changes: foreign,
    acknowledge: () => setSeen(new Set(store.events.map((e) => eventKey(e)))),
  };
}

const MAX_LINES = 8;

export function StoreChangeNotice({
  store,
  changes,
  onAcknowledge,
}: {
  store: AdminStorefrontDetail;
  changes: AdminStorefrontEvent[];
  onAcknowledge: () => void;
}) {
  if (changes.length === 0) return null;
  const names = productNameMap(store);
  const shown = changes.slice(0, MAX_LINES);
  return (
    <div
      role="alert"
      className="flex flex-col gap-10 px-14 py-12 border border-warning bg-warning-wash type-body-sm text-ink"
    >
      <span className="flex items-center gap-8 type-label-sm">
        <RefreshCw size={14} aria-hidden /> This store changed since you opened it
      </span>
      <ul className="flex flex-col gap-4 pl-2">
        {shown.map((event) => (
          <li key={eventKey(event)} className="flex flex-wrap gap-x-8 leading-130">
            <span className="text-ink break-words">
              {event.kind === 'change' && event.field && /^design\.[a-z_]+\.items$/.test(event.field)
                ? faqSentence({ field: event.field, old: event.old_value, new: event.new_value })
                : eventSentence(event, names)}
            </span>
            <span className="text-ink-muted">
              {event.actor ?? 'Someone'} · {shortDateTime(event.created_at)}
            </span>
          </li>
        ))}
        {changes.length > shown.length && (
          <li className="text-ink-muted">
            And {changes.length - shown.length} more (see History).
          </li>
        )}
      </ul>
      <div className="flex flex-wrap items-center justify-between gap-10">
        <span className="text-ink-secondary">
          The page now shows the current store. Approve, reject and pause stay off until you have
          looked it over.
        </span>
        <ActionButton tone="primary" onClick={onAcknowledge}>
          I&apos;ve seen these changes
        </ActionButton>
      </div>
    </div>
  );
}
